import { useCallback } from 'react';

import { useMyCourseData } from 'Domain/MyCourseData';
import useMyExternalProfile from 'Routes/Classroom/Domain/useMyExternalProfile';
import { useClassroomContext } from 'Context/ClassroomContext';
import postMessageHelper from 'Util/Message/postMessageHelper';
import { TOPIC } from 'Util/Message/TopicName';

import { getClassroomInfo } from './getEventLabel';
import useTracking from './useTracking';
import { useExceptionTracking } from './useExceptionTracking';
import { formatBangkokTime } from './utils';

const CATEGORY = 'Classroom';
const CATEGORY_FAILED = 'Classroom Failed';

const CLASSROOM_ACTIONS = {
  TOC_CLICKED: 'Toggle ToC open',
  SECTION_GROUP_CLICKED: 'SectionGroup clicked',
  SECTION_CLICKED: 'Section clicked',
  SUBSECTION_CLICKED: 'Subsection clicked',
  DOCUMENT_CLICKED: 'Toggle document panel open',
  DOCUMENT_TAB_CLICKED: 'Select document tab',
  DOWNLOAD_ITEM: 'Download item',
  SUBSECTION_VIDEO_COMPLETE: 'Subsection video complete',
  SUBSECTION_VIDEO_COMPLETE_CLIENT: 'Subsection video complete client',
  SUBSECTION_VIDEO_COMPLETE_ERROR: 'Subsection video complete error',
  SUBSECTION_VIDEO_ON_VIDEO_PLAYER_ERROR: 'Subsection video on video player error',
};

const defaultCourseMode = 'TRADITIONAL';
const fallbackValue = '-';

export const getClassroomToggleEvent = (action) => ({
  category: CATEGORY,
  action,
});

export const getClassroomEvent = (action, label) => ({
  category: CATEGORY,
  action,
  label,
});

export const getClassroomError = (type, description) => ({
  description: `[${CATEGORY_FAILED}: ${type}] ${formatBangkokTime()} ${description}`,
  fatal: true,
});

export const genDownloadItemEventLabel = ({
  userProvider,
  subject,
  courseCode,
  course,
  subsectionId,
  itemTitle,
}) => {
  const { sectionNumber, subSectionNumber } = getClassroomInfo(course, subsectionId);
  return `BU:${userProvider}|Subject:${subject}|Course:${courseCode}|Section:${sectionNumber}|SubSection:${subSectionNumber}|Item:${itemTitle}`;
};

function useClassroomTracking() {
  const { logAction } = useTracking();
  const { logError } = useExceptionTracking();
  const { data: userData } = useMyExternalProfile({ fetchPolicy: 'cache-only' });
  const { data: myCourseData } = useMyCourseData();
  const { courseData, currentSubsectionId, courseId } = useClassroomContext();
  const courseCode = myCourseData?.courseInfo.courseCode;
  const courseMode = courseData?.courseMode ?? defaultCourseMode;

  const logTocToggleClick = useCallback(() => {
    logAction(getClassroomToggleEvent(CLASSROOM_ACTIONS.TOC_CLICKED));
    postMessageHelper(TOPIC.CLASSROOM_TOC_TOGGLE_CLICKED, {
      courseId: courseId ?? fallbackValue,
      courseCode: courseCode ?? fallbackValue,
      courseMode,
    });
  }, [courseCode, courseId, courseMode, logAction]);

  const logDocumentToggleClick = useCallback(() => {
    logAction(getClassroomToggleEvent(CLASSROOM_ACTIONS.DOCUMENT_CLICKED));
    postMessageHelper(TOPIC.CLASSROOM_DOCUMENT_TOGGLE_CLICKED, {
      courseId: courseId ?? fallbackValue,
      courseCode: courseCode ?? fallbackValue,
      courseMode,
    });
  }, [courseCode, courseId, courseMode, logAction]);

  const logDocumentTabClick = useCallback(
    (tabTitle) => {
      logAction(getClassroomEvent(CLASSROOM_ACTIONS.DOCUMENT_TAB_CLICKED, tabTitle));
    },
    [logAction]
  );

  const logSectionGroupClick = useCallback(
    (sectionGroupTitle) => {
      logAction(getClassroomEvent(CLASSROOM_ACTIONS.SECTION_GROUP_CLICKED, sectionGroupTitle));
    },
    [logAction]
  );

  const logSectionClick = useCallback(
    (sectionTitle) => {
      logAction(getClassroomEvent(CLASSROOM_ACTIONS.SECTION_CLICKED, sectionTitle));
      postMessageHelper(TOPIC.CLASSROOM_SECTION_CLICKED, {
        courseId: courseId ?? fallbackValue,
        courseCode: courseCode ?? fallbackValue,
        sectionTitle: sectionTitle ?? fallbackValue,
        courseMode,
      });
    },
    [courseCode, courseId, courseMode, logAction]
  );

  const logSubsectionClick = useCallback(
    (subsectionTitle) => {
      logAction(getClassroomEvent(CLASSROOM_ACTIONS.SUBSECTION_CLICKED, subsectionTitle));
      postMessageHelper(TOPIC.CLASSROOM_SUBSECTION_CLICKED, {
        courseId: courseId ?? fallbackValue,
        courseCode: courseCode ?? fallbackValue,
        subsectionTitle: subsectionTitle ?? fallbackValue,
        courseMode,
      });
    },
    [courseCode, courseId, courseMode, logAction]
  );

  const logDownloadItemClick = useCallback(
    (itemTitle) => {
      logAction(
        getClassroomEvent(
          CLASSROOM_ACTIONS.DOWNLOAD_ITEM,
          genDownloadItemEventLabel({
            userProvider: userData?.myExternalProfile?.providerName ?? fallbackValue,
            subject: myCourseData?.courseInfo?.courseLevel ?? fallbackValue,
            courseCode: myCourseData?.courseInfo?.courseCode ?? fallbackValue,
            course: courseData ?? fallbackValue,
            subsectionId: currentSubsectionId ?? fallbackValue,
            itemTitle: itemTitle ?? fallbackValue,
            courseMode,
          })
        )
      );
      postMessageHelper(TOPIC.CLASSROOM_DOWNLOAD_ITEM, {
        courseId: courseId ?? fallbackValue,
        courseCode: courseCode ?? fallbackValue,
        itemTitle: itemTitle ?? fallbackValue,
        courseMode,
      });
    },
    [
      courseCode,
      courseData,
      courseId,
      courseMode,
      currentSubsectionId,
      logAction,
      myCourseData?.courseInfo?.courseCode,
      myCourseData?.courseInfo?.courseLevel,
      userData?.myExternalProfile?.providerName,
    ]
  );

  const logSubsectionVideoTypeComplete = useCallback(
    ({
      videoWatchedDuration,
      videoCompletionThreshold,
      videoTotalDuration,
      serverTimestamp,
      subsectionTitle,
      subsectionContentType,
      videoProvider,
    }) => {
      logAction(
        getClassroomEvent(CLASSROOM_ACTIONS.SUBSECTION_VIDEO_COMPLETE, currentSubsectionId)
      );
      postMessageHelper(TOPIC.CLASSROOM_SUBSECTION_VIDEO_COMPLETE, {
        courseId: courseId ?? fallbackValue,
        revision: courseData?.courseRevision ?? fallbackValue,
        subsectionId: currentSubsectionId ?? fallbackValue,
        subsectionTitle: subsectionTitle ?? fallbackValue,
        subsectionContentType: subsectionContentType ?? fallbackValue,
        clientTimestamp: formatBangkokTime(),
        serverTimestamp: serverTimestamp ? formatBangkokTime(serverTimestamp) : fallbackValue,
        videoWatchedDuration: videoWatchedDuration ?? fallbackValue,
        videoCompletionThreshold: videoCompletionThreshold ?? fallbackValue,
        videoTotalDuration: videoTotalDuration ?? fallbackValue,
        videoProvider: videoProvider ?? fallbackValue,
        courseMode,
      });
    },
    [courseData?.courseRevision, courseMode, courseId, currentSubsectionId, logAction]
  );

  const logSubsectionVideoTypeCompleteClient = useCallback(
    ({
      videoWatchedDuration,
      videoCompletionThreshold,
      videoTotalDuration,
      subsectionTitle,
      subsectionContentType,
      videoProvider,
    }) => {
      logAction(
        getClassroomEvent(CLASSROOM_ACTIONS.SUBSECTION_VIDEO_COMPLETE_CLIENT, currentSubsectionId)
      );
      postMessageHelper(TOPIC.CLASSROOM_SUBSECTION_VIDEO_COMPLETE_CLIENT, {
        courseId: courseId ?? fallbackValue,
        revision: courseData?.courseRevision ?? fallbackValue,
        subsectionId: currentSubsectionId ?? fallbackValue,
        subsectionTitle: subsectionTitle ?? fallbackValue,
        subsectionContentType: subsectionContentType ?? fallbackValue,
        clientTimestamp: formatBangkokTime(),
        videoWatchedDuration: videoWatchedDuration ?? fallbackValue,
        videoCompletionThreshold: videoCompletionThreshold ?? fallbackValue,
        videoTotalDuration: videoTotalDuration ?? fallbackValue,
        videoProvider: videoProvider ?? fallbackValue,
        courseMode,
      });
    },
    [courseData?.courseRevision, courseMode, courseId, currentSubsectionId, logAction]
  );

  const logSubsectionVideoTypeCompleteError = useCallback(
    ({
      videoWatchedDuration,
      videoCompletionThreshold,
      videoTotalDuration,
      subsectionTitle,
      subsectionContentType,
      errorMessage,
      videoProvider,
    }) => {
      logError(
        getClassroomError(CLASSROOM_ACTIONS.SUBSECTION_VIDEO_COMPLETE_ERROR, currentSubsectionId)
      );
      postMessageHelper(TOPIC.CLASSROOM_SUBSECTION_VIDEO_COMPLETE_ERROR, {
        courseId: courseId ?? fallbackValue,
        revision: courseData?.courseRevision ?? fallbackValue,
        subsectionId: currentSubsectionId ?? fallbackValue,
        subsectionTitle: subsectionTitle ?? fallbackValue,
        subsectionContentType: subsectionContentType ?? fallbackValue,
        clientTimestamp: formatBangkokTime(),
        errorMessage: errorMessage ?? fallbackValue,
        videoWatchedDuration: videoWatchedDuration ?? fallbackValue,
        videoCompletionThreshold: videoCompletionThreshold ?? fallbackValue,
        videoTotalDuration: videoTotalDuration ?? fallbackValue,
        videoProvider: videoProvider ?? fallbackValue,
        courseMode,
      });
    },
    [logError, currentSubsectionId, courseId, courseData?.courseRevision, courseMode]
  );

  const logSubsectionVideoOnVideoPlayerError = useCallback(
    ({
      subsectionTitle,
      subsectionContentType,
      errorMessage,
      videoProvider,
      videoWatchedDuration,
      videoCompletionThreshold,
      videoTotalDuration,
    }) => {
      logError(
        getClassroomError(
          CLASSROOM_ACTIONS.SUBSECTION_VIDEO_ON_VIDEO_PLAYER_ERROR,
          currentSubsectionId
        )
      );
      postMessageHelper(TOPIC.CLASSROOM_SUBSECTION_VIDEO_ON_VIDEO_PLAYER_ERROR, {
        courseId: courseId ?? fallbackValue,
        revision: courseData?.courseRevision ?? fallbackValue,
        subsectionId: currentSubsectionId ?? fallbackValue,
        subsectionTitle: subsectionTitle ?? fallbackValue,
        subsectionContentType: subsectionContentType ?? fallbackValue,
        clientTimestamp: formatBangkokTime(),
        errorMessage: errorMessage ?? fallbackValue,
        videoWatchedDuration: videoWatchedDuration ?? fallbackValue,
        videoCompletionThreshold: videoCompletionThreshold ?? fallbackValue,
        videoTotalDuration: videoTotalDuration ?? fallbackValue,
        videoProvider: videoProvider ?? fallbackValue,
        courseMode,
      });
    },
    [courseData?.courseRevision, courseMode, courseId, currentSubsectionId, logError]
  );

  const logSubsectionVideoInteraction = useCallback(
    (info) => {
      // GA should only record user-driven interactions. Actions triggered by the system
      // (e.g. odah commanding play/pause/seek, or auto-resume) arrive with no interactionMethod
      // because the player only tags user input (mouse/keyboard/touch). Skip those.
      const USER_INITIATED_ACTIONS = [
        'play',
        'pause',
        'seek_forward',
        'seek_backward',
        'volume_change',
        'speed_change',
      ];
      if (USER_INITIATED_ACTIONS.includes(info.action) && !info.interactionMethod) {
        return;
      }
      let actionParams = {};
      if (
        info.action === 'seek_forward' ||
        info.action === 'seek_backward' ||
        info.action === 'replay'
      ) {
        actionParams = {
          seekFromSec: info.seekFromSec,
          seekToSec: info.seekToSec,
        };
      } else if (info.action === 'volume_change') {
        actionParams = {
          changeVolumeFrom: info.changeVolumeFrom,
          changeVolumeTo: info.changeVolumeTo,
        };
      } else if (info.action === 'speed_change') {
        actionParams = {
          speed: info.speed,
        };
      }
      postMessageHelper(TOPIC.CLASSROOM_SUBSECTION_VIDEO_INTERACTION, {
        courseId: courseId ?? fallbackValue,
        revision: courseData?.courseRevision ?? fallbackValue,
        subsectionId: currentSubsectionId ?? fallbackValue,
        clientTimestamp: formatBangkokTime(),
        action: info.action,
        videoProvider: info.videoProvider,
        videoCurrentSec: info.videoCurrentSec,
        videoThresholdSec: info.videoThresholdSec,
        videoDurationSec: info.videoDurationSec,
        videoCurrentPercent: info.videoCurrentPercent,
        videoTotalWatchSec: info.videoTotalWatchSec,
        videoCompletionStatus: info.videoCompletionStatus,
        interactionMethod: info.interactionMethod,
        courseMode,
        ...actionParams,
      });
    },
    [courseData?.courseRevision, courseMode, courseId, currentSubsectionId]
  );

  const logSubsectionContentClick = useCallback(
    ({ subsectionTitle, subsectionContentType }) => {
      logAction(
        getClassroomEvent(
          CLASSROOM_ACTIONS.CLASSROOM_SUBSECTION_CONTENT_CLICKED,
          currentSubsectionId
        )
      );
      postMessageHelper(TOPIC.CLASSROOM_SUBSECTION_CONTENT_CLICKED, {
        courseId: courseId ?? fallbackValue,
        revision: courseData?.courseRevision ?? fallbackValue,
        subsectionId: currentSubsectionId ?? fallbackValue,
        subsectionTitle: subsectionTitle ?? fallbackValue,
        subsectionContentType: subsectionContentType ?? fallbackValue,
        clientTimestamp: formatBangkokTime(),
        courseMode,
      });
    },
    [courseData?.courseRevision, courseMode, courseId, currentSubsectionId, logAction]
  );

  return {
    logTocToggleClick,
    logDocumentToggleClick,
    logDocumentTabClick,
    logSectionGroupClick,
    logSectionClick,
    logSubsectionClick,
    logDownloadItemClick,
    logSubsectionVideoTypeComplete,
    logSubsectionVideoTypeCompleteClient,
    logSubsectionContentClick,
    logSubsectionVideoTypeCompleteError,
    logSubsectionVideoOnVideoPlayerError,
    logSubsectionVideoInteraction,
  };
}

export { useClassroomTracking };
